<template>
  <validation-observer ref="form">
    <form @submit.prevent="save">
      <section-card
        icon="fa-palette"
        color="purple"
        title="Custom Branding"
        desc="Configure how your sequence emails look and when they are delivered."
      >
        <info-banner
          v-if="settings.use_as_default"
          color="orange"
          description="These branding options are managed by your Media Library defaults, so there's nothing to configure here."
        />

        <div class="branding-grid">
          <div class="branding-panel">
            <h4 class="branding-panel__title">
              <i class="fa-regular fa-image"></i>
              Email Sequence Logo
            </h4>

            <c-upload-image
              id="email_sequence_logo_url"
              title=""
              :image_url="settings.email_sequence_logo_url"
              :uuid="settings.email_sequence_logo"
              helpText=""
              :existingImagesUrl="mediaLibraryUrl"
              @upload="(file) => (uploadLogo(file))"
              @remove="removeLogo"
              data-crop="free, 1:1, 3:4, 16:9"
            />
          </div>

          <div class="branding-panel">
            <h4 class="branding-panel__title">
              <i class="fa-regular fa-envelope-open-text"></i>
              Email Appearance
            </h4>

            <div class="color-field">
              <label class="control-label">Background Color for Email Header</label>
              <div class="color-field__row">
                <input
                  type="color"
                  class="color-field__swatch"
                  v-model="settings.style.header_color"
                />
                <input
                  type="text"
                  class="form-control color-field__hex"
                  v-model="settings.style.header_color"
                  maxlength="7"
                  spellcheck="false"
                />
              </div>
            </div>

            <div class="color-field">
              <label class="control-label">Background Color for Email Content</label>
              <div class="color-field__row">
                <input
                  type="color"
                  class="color-field__swatch"
                  v-model="settings.style.content_color"
                />
                <input
                  type="text"
                  class="form-control color-field__hex"
                  v-model="settings.style.content_color"
                  maxlength="7"
                  spellcheck="false"
                />
              </div>
            </div>

            <validation-provider vid="timezone" v-slot="{ errors }">
              <c-select
                label="Timezone"
                :options="timezoneOptions"
                label-key="text"
                value-key="value"
                v-model="settings.timezone"
                :error="errors[0]"
                :required-mark="true"
                :disabled="type === 'event'"
                help="Used to determine when sequence emails are sent."
              />
            </validation-provider>
          </div>
        </div>

        <div class="branding-footer">
          <c-button
            type="submit"
            theme="success"
            icon="fa-save"
            :show-indicator="showIndicator"
          >Save Settings</c-button>
        </div>
      </section-card>
    </form>
  </validation-observer>
</template>

<script>
import { fetchData } from '../../api/network';

export default {
  props: {
    type: {
      type: String,
      required: true
    },
    showIndicator: {
      type: Boolean,
      default: false
    },
    settings: {
      type: Object,
      default: () => {}
    },
    mediaLibraryUrl: {
      type: String,
      default: ''
    }
  },
  emits: [
    'save'
  ],
  data() {
    return {
      timezoneOptions: []
    }
  },
  mounted() {
    // Ensure reactivity for timezone field, even when API response did not include it
    if (this.settings && this.settings.timezone === undefined) {
      this.$set(this.settings, 'timezone', 'America/New_York');
    }

    this.fetchTimezones();
  },
  methods: {
    fetchTimezones() {
      const url = `/api/timezones`;

      fetchData(url)
        .then(res => {
          this.timezoneOptions = res.data || [];
        })
        .catch(err => {
          if (err.flash) {
            notifier.error('Error', err.flash);
          }
        });
    },

    uploadLogo(file) {
      this.$set(this.settings, "email_sequence_logo_url", `${file.cdnUrl || file.url}-/format/auto/-/preview/800x600/-/stretch/off/`);
      this.$set(this.settings, "email_sequence_logo", file.uuid);
      this.$set(this.settings, "uploadcare_email_sequence_logo", file);
    },

    removeLogo() {
      this.$set(this.settings, "email_sequence_logo_url", null);
      this.$set(this.settings, "email_sequence_logo", null);
      this.$set(this.settings, "uploadcare_email_sequence_logo", null);
    },

    save() {
      this.$refs.form.validate().then(success => {
        if (!success) {
          return;
        }

        this.$emit("save", this.settings);
      });
    }
  }
}
</script>

<style scoped lang="scss">
.branding-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  align-items: stretch;
}

@media (max-width: 991px) {
  .branding-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
}

.branding-panel {
  padding: 18px;
  background: #fafbfc;
  border: 1px solid #ececec;
  border-radius: 8px;

  &__title {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0 0 16px;
    padding-bottom: 10px;
    font-size: 14px;
    font-weight: 600;
    color: #374151;
    border-bottom: 1px solid #eef0f2;

    i {
      color: #8b5cf6;
    }
  }

  // Drop c-upload-image's own card chrome so the panel is the only frame.
  ::v-deep .c-upload-image {
    border: none;
    padding: 0;
    margin: 0;
  }

  ::v-deep .c-upload-image h4 {
    display: none;
  }
}

.color-field {
  margin-bottom: 16px;

  .control-label {
    display: block;
    margin-bottom: 6px;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__swatch {
    flex-shrink: 0;
    width: 46px;
    height: 38px;
    padding: 4px;
    background: #fff;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    cursor: pointer;

    &::-webkit-color-swatch-wrapper {
      padding: 0;
    }
    &::-webkit-color-swatch {
      border: none;
      border-radius: 4px;
    }
    &::-moz-color-swatch {
      border: none;
      border-radius: 4px;
    }
  }

  &__hex {
    width: 110px;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    text-transform: uppercase;
  }
}

.branding-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}
</style>
